import * as React from 'react';
import { snap, story, visualize } from 'storybook-snapper';
import {
  Checkbox,
  CheckboxTheme,
  CheckboxProps,
  CheckboxVerticalAlignment,
  CheckmarkStyle,
} from '..';
import { classes } from './Checkbox.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper';
import { VisualAsync } from '../../test/visual/VisualAsync';
import { checkboxDriverFactory } from './Checkbox.driver';

const getElementFromBelow = () => (
  <div className={classes.elementBelow}>
    An element below to show the spacing
  </div>
);

class CheckboxVisual extends React.Component<Partial<CheckboxProps>> {
  render() {
    return (
      <Checkbox
        {...this.props}
        label={this.props.label || 'Amazing'}
        onChange={() => {}}
      />
    );
  }
}

visualize('Checkbox', () => {
  story('focus', () => {
    const its = [
      {
        it: 'default / default / no suffix',
        props: {
          theme: CheckboxTheme.Default,
          className: '',
        },
      },
      {
        it: 'default / default / "some suffix"',
        props: {
          theme: CheckboxTheme.Default,
          className: '',
          suffix: '"some suffix"',
        },
      },
      {
        it: 'default / default with overrides / no suffix',
        props: {
          theme: CheckboxTheme.Default,
          className: classes.root,
        },
      },
      {
        it: 'default / default with overrides / "some suffix"',
        props: {
          theme: CheckboxTheme.Default,
          className: classes.root,
          suffix: '"some suffix"',
        },
      },
      {
        it: 'box / default / no suffix',
        props: {
          theme: CheckboxTheme.Box,
          className: '',
        },
      },
      {
        it: 'box / default / "some suffix"',
        props: {
          theme: CheckboxTheme.Box,
          className: '',
          suffix: '"some suffix"',
        },
      },
      {
        it: 'box / default with overrides / no suffix',
        props: {
          theme: CheckboxTheme.Box,
          className: classes.root,
        },
      },
      {
        it: 'box / default with overrides / "some suffix"',
        props: {
          theme: CheckboxTheme.Box,
          className: classes.root,
          suffix: '"some suffix"',
        },
      },
    ];
    its.forEach(({ it, props }) =>
      snap(it, (done) => (
        <VisualAsync
          driverFactory={checkboxDriverFactory}
          onDone={done}
          action={async (driver) => {
            (await driver.getLabel().getNative()).click();
          }}
        >
          <CheckboxVisual {...props} />
        </VisualAsync>
      )),
    );
  });

  story('default', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(`${theme} / default`, <CheckboxVisual theme={theme} />);
    });

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / default with overrides`,
        <CheckboxVisual theme={theme} className={classes.root} />,
      );
    });
  });

  story('checked', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(`${theme} / checked`, <CheckboxVisual theme={theme} checked />);
    });

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / checked with overrides`,
        <CheckboxVisual theme={theme} checked className={classes.root} />,
      );
    });
  });

  story('old error', () => {
    snap(
      `${CheckboxTheme.Default} / without error message`,
      <CheckboxVisual theme={CheckboxTheme.Default} error />,
    );

    snap(
      `${CheckboxTheme.Default} / with error message`,
      <CheckboxVisual
        theme={CheckboxTheme.Default}
        error
        errorMessage="error message"
      />,
    );
    snap(
      `${CheckboxTheme.Box} / without error message`,
      <CheckboxVisual theme={CheckboxTheme.Box} error />,
    );
    snap(
      `${CheckboxTheme.Box} / with error message`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        error
        errorMessage="error message"
      />,
    );

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / without error message with overrides`,
        <CheckboxVisual theme={theme} className={classes.root} error />,
      );
    });

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / with error message with overrides`,
        <CheckboxVisual
          className={classes.root}
          theme={theme}
          error
          errorMessage="error message"
        />,
      );
    });
  });

  story('new error', () => {
    snap(
      `${CheckboxTheme.Default} / without error message`,
      <CheckboxVisual theme={CheckboxTheme.Default} error newErrorMessage />,
    );
    snap(
      `${CheckboxTheme.Default} / with error message`,
      <CheckboxVisual
        theme={CheckboxTheme.Default}
        error
        newErrorMessage
        errorMessage="error message"
      />,
    );
    snap(
      `${CheckboxTheme.Box} / without error message`,
      <CheckboxVisual theme={CheckboxTheme.Box} error newErrorMessage />,
    );
    snap(
      `${CheckboxTheme.Box} / with error message`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        error
        newErrorMessage
        errorMessage="error message"
      />,
    );

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / without error message with overrides`,
        <>
          <CheckboxVisual
            theme={theme}
            className={classes.root}
            error
            newErrorMessage
          />
          {getElementFromBelow()}
        </>,
      );
    });

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / with error message with overrides`,
        <>
          <CheckboxVisual
            theme={theme}
            className={classes.root}
            error
            newErrorMessage
            errorMessage="error message"
          />
          {getElementFromBelow()}
        </>,
      );
    });
  });

  story('new error / ErrorAppearance - BackgroundAndBorder', () => {
    snap(
      `${CheckboxTheme.Default} / without error message`,
      <CheckboxVisual
        errorAppearance={ErrorAppearance.BackgroundAndBorder}
        theme={CheckboxTheme.Default}
        error
        newErrorMessage
      />,
    );
    snap(
      `${CheckboxTheme.Default} / with error message`,
      <CheckboxVisual
        errorAppearance={ErrorAppearance.BackgroundAndBorder}
        theme={CheckboxTheme.Default}
        error
        newErrorMessage
        errorMessage="error message"
      />,
    );
    snap(
      `${CheckboxTheme.Box} / without error message`,
      <CheckboxVisual
        errorAppearance={ErrorAppearance.BackgroundAndBorder}
        theme={CheckboxTheme.Box}
        error
        newErrorMessage
      />,
    );
    snap(
      `${CheckboxTheme.Box} / with error message`,
      <CheckboxVisual
        errorAppearance={ErrorAppearance.BackgroundAndBorder}
        theme={CheckboxTheme.Box}
        error
        newErrorMessage
        errorMessage="error message"
      />,
    );

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / without error message with overrides`,
        <>
          <CheckboxVisual
            errorAppearance={ErrorAppearance.BackgroundAndBorder}
            theme={theme}
            className={classes.root}
            error
            newErrorMessage
          />
          {getElementFromBelow()}
        </>,
      );
    });

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / with error message with overrides`,
        <>
          <CheckboxVisual
            errorAppearance={ErrorAppearance.BackgroundAndBorder}
            theme={theme}
            className={classes.root}
            error
            newErrorMessage
            errorMessage="error message"
          />
          {getElementFromBelow()}
        </>,
      );
    });
  });

  story('disabled', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(`${theme} / disabled`, <CheckboxVisual theme={theme} disabled />);
      snap(
        `${theme} / disabled / checked`,
        <CheckboxVisual theme={theme} disabled checked />,
      );
    });

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / disabled with overrides`,
        <CheckboxVisual theme={theme} className={classes.root} disabled />,
      );
      snap(
        `${theme} / disabled / checked with overrides`,
        <CheckboxVisual
          theme={theme}
          className={classes.root}
          disabled
          checked
        />,
      );
    });
  });

  story('indeterminate', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / indeterminate`,
        <CheckboxVisual theme={theme} indeterminate />,
      );
    });
    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / indeterminate with overrides`,
        <CheckboxVisual theme={theme} className={classes.root} indeterminate />,
      );
    });
  });

  story('required', () => {
    snap(
      `${CheckboxTheme.Box} / required`,
      <CheckboxVisual theme={CheckboxTheme.Box} required={true} />,
    );
  });

  story('suffix', () => {
    snap(
      `${CheckboxTheme.Box} / suffix (string)`,
      <CheckboxVisual theme={CheckboxTheme.Box} suffix="$50,000" />,
    );
    snap(
      `${CheckboxTheme.Box} / suffix (reactNode)`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        suffix={
          <span style={{ backgroundColor: 'red', padding: '3px' }}>$10</span>
        }
      />,
    );
    snap(
      `${CheckboxTheme.Box} / suffix (string) with overrides`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        className={classes.root}
        suffix="$50,000"
      />,
    );
    snap(
      `${CheckboxTheme.Box} / suffix (reactNode) with overrides`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        className={classes.root}
        suffix={
          <span style={{ backgroundColor: 'red', padding: '3px' }}>$10</span>
        }
      />,
    );
  });

  story('long', () => {
    snap(
      `${CheckboxTheme.Box} / long`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        label="It is a period of civil war. Rebel spaceships, striking from a hidden base, have won their first victory against the evil Galactic Empire. During the battle, Rebel spies managed to steal secret plans to the Empire's ultimate weapon, the DEATH STAR, an armored space station with enough power to destroy an entire planet."
      />,
    );
    snap(
      `${CheckboxTheme.Box} / long / suffix`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        suffix="$50,000"
        label="It is a period of civil war. Rebel spaceships, striking from a hidden base, have won their first victory against the evil Galactic Empire. During the battle, Rebel spies managed to steal secret plans to the Empire's ultimate weapon, the DEATH STAR, an armored space station with enough power to destroy an entire planet."
      />,
    );
    snap(
      `${CheckboxTheme.Box} / long with overrides`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        className={classes.root}
        label="It is a period of civil war. Rebel spaceships, striking from a hidden base, have won their first victory against the evil Galactic Empire. During the battle, Rebel spies managed to steal secret plans to the Empire's ultimate weapon, the DEATH STAR, an armored space station with enough power to destroy an entire planet."
      />,
    );
    snap(
      `${CheckboxTheme.Box} / long / suffix with overrides`,
      <CheckboxVisual
        theme={CheckboxTheme.Box}
        className={classes.root}
        suffix="$50,000"
        label="It is a period of civil war. Rebel spaceships, striking from a hidden base, have won their first victory against the evil Galactic Empire. During the battle, Rebel spies managed to steal secret plans to the Empire's ultimate weapon, the DEATH STAR, an armored space station with enough power to destroy an entire planet."
      />,
    );
  });

  story('with border overrides', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme}`,
        <CheckboxVisual theme={theme} className={classes.borderOverrides} />,
      );
    });
  });

  story('size', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(`${theme} / default size`, <CheckboxVisual theme={theme} />);
      snap(
        `${theme} / custom size`,
        <CheckboxVisual theme={theme} className={classes.size} />,
      );
    });
    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / checked / default size`,
        <CheckboxVisual theme={theme} checked />,
      );
      snap(
        `${theme} / checked / custom size`,
        <CheckboxVisual theme={theme} className={classes.size} checked />,
      );
    });
  });

  story('vertical alignment', () => {
    Object.values(CheckboxTheme).map((theme) => {
      const label =
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.';

      snap(
        `${theme} / long label / default`,
        <div style={{ width: '500px' }}>
          <CheckboxVisual theme={theme} label={label} />,
        </div>,
      );
      snap(
        `${theme} / long label / center`,
        <div style={{ width: '500px' }}>
          <CheckboxVisual
            theme={theme}
            label={label}
            verticalAlignment={CheckboxVerticalAlignment.Center}
          />
        </div>,
      );
      snap(
        `${theme} / long label / top`,
        <div style={{ width: '500px' }}>
          <CheckboxVisual
            theme={theme}
            label={label}
            verticalAlignment={CheckboxVerticalAlignment.Top}
          />
        </div>,
      );
    });

    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / short label / default`,
        <CheckboxVisual theme={theme} />,
      );
      snap(
        `${theme} / short label / center`,
        <CheckboxVisual
          theme={theme}
          verticalAlignment={CheckboxVerticalAlignment.Center}
        />,
      );
      snap(
        `${theme} / short label / top`,
        <CheckboxVisual
          theme={theme}
          verticalAlignment={CheckboxVerticalAlignment.Top}
        />,
      );
    });
  });

  story('checkmark style', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(`${theme} / default`, <CheckboxVisual theme={theme} checked />);
      snap(
        `${theme} / regular`,
        <CheckboxVisual
          theme={theme}
          checkmarkStyle={CheckmarkStyle.Regular}
          checked
        />,
      );
      snap(
        `${theme} / medium`,
        <CheckboxVisual
          theme={theme}
          checkmarkStyle={CheckmarkStyle.Medium}
          checked
        />,
      );
      snap(
        `${theme} / bold`,
        <CheckboxVisual
          theme={theme}
          checkmarkStyle={CheckmarkStyle.Bold}
          checked
        />,
      );
    });
  });

  story('RTL', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / default`,
        <div dir="rtl">
          <CheckboxVisual theme={theme} />{' '}
        </div>,
      );
    });
  });

  story('inline spacing', () => {
    Object.values(CheckboxTheme).map((theme) => {
      snap(
        `${theme} / increased spacing`,
        <CheckboxVisual theme={theme} className={classes.inlineSpacing} />,
      );
    });
  });

  story('wst variables', () => {
    const Checkboxes = ({
      variables,
    }: {
      variables: Record<`--wst-${string}`, string>;
    }) => {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', ...variables }}>
          <CheckboxVisual label="Unchecked" />
          <CheckboxVisual label="Checked" checked />
          <CheckboxVisual
            label="Error state"
            error
            errorMessage="Error message"
          />
          <CheckboxVisual
            label="Checked error state"
            error
            checked
            errorMessage="Error message"
          />
          <CheckboxVisual label="Disabled" disabled />
          <CheckboxVisual label="Disabled and checked" disabled checked />

          <CheckboxVisual theme={CheckboxTheme.Box} label="Unchecked" />
          <CheckboxVisual theme={CheckboxTheme.Box} label="Checked" checked />
          <CheckboxVisual
            theme={CheckboxTheme.Box}
            label="Error state"
            error
            errorMessage="Error message"
          />
          <CheckboxVisual
            theme={CheckboxTheme.Box}
            label="Checked error state"
            error
            checked
            errorMessage="Error message"
          />
          <CheckboxVisual theme={CheckboxTheme.Box} label="Disabled" disabled />
          <CheckboxVisual
            theme={CheckboxTheme.Box}
            label="Disabled and checked"
            disabled
            checked
          />
        </div>
      );
    };

    snap('primary background color', () => (
      <Checkboxes
        variables={{ '--wst-primary-background-color-rgb': '0,255,0' }}
      />
    ));

    snap('links and actions color', () => (
      <Checkboxes
        variables={{
          '--wst-links-and-actions-color-rgb': '0,255,0',
        }}
      />
    ));

    snap('system disabled color', () => (
      <Checkboxes
        variables={{
          '--wst-system-disabled-color-rgb': '0,255,0',
        }}
      />
    ));

    snap('system error color', () => (
      <Checkboxes
        variables={{
          '--wst-system-error-color-rgb': '0,255,0',
        }}
      />
    ));

    snap('paragraph 2', () => (
      <Checkboxes
        variables={{
          '--wst-paragraph-2-color-rgb': '0,255,0',
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-paragraph-2-font-size': '22px',
          '--wst-paragraph-2-font-weight': 'bold',
          '--wst-paragraph-2-font-style': 'italic',
          '--wst-paragraph-2-font-variant': 'small-caps',
        }}
      />
    ));
  });
});
